import { useMutation, useQuery } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { CheckedTableValue } from '@/components_deprecated'
import groupBy from 'lodash/groupBy'
import { BATCH_PATCH_NOTIFICATION_SETTINGS_MUTATION } from '@/modules/companies'
import { CURRENT_COMPANY_NOTIFICATION_SETTINGS_QUERY } from '@/modules/companies/queries'
import type {
  BatchPatchNotificationSettingInput,
  BatchPatchNotificationSettingsMutation,
  BatchPatchNotificationSettingsMutationVariables,
} from '@/modules/companies/types.graphql'
import { useState } from 'react'
import { useNavigateConfirm, useOnErrorAuto } from '@/utils/hooks'
import {
  DEFAULT_REMINDER_WEEKS_BEFORE,
  parseReminderWeeks,
} from './reminder-weeks'

export function useNotificationSettings() {
  const translations = useTranslate({
    prompt: {
      unsavedChangesNavigate: 'common.you-have-unsaved-changes-navigate',
    },
    success: 'settings.toasts.update-notifications-success',
    error: 'settings.errors.update-notifications',
    invalidReminderWeeks: 'settings.errors.driver-card-reminder-weeks',
  })

  const exportedTranslations = useTranslate({
    notifications: 'common.notifications',
    info: 'settings.info.notifications',

    prompt: {
      message: 'common.you-have-unsaved-changes',
    },
  })

  const addToast = useToast()
  const onErrorAuto = useOnErrorAuto()

  const [notificationsDiff, setNotificationsDiff] = useState<
    CheckedTableValue[]
  >([])
  const [reminderWeeksDraft, setReminderWeeksDraft] = useState<string | null>(
    null
  )

  const { data: queryData, loading: queryLoading } = useQuery(
    CURRENT_COMPANY_NOTIFICATION_SETTINGS_QUERY,
    {
      onError: onErrorAuto(),
    }
  )

  const driverCardSetting =
    queryData?.currentCompany?.notificationSettings.find(
      setting => setting.name === 'DRIVER_CARD_WILL_EXPIRE'
    ) ?? null
  const savedReminderWeeks =
    driverCardSetting?.reminderWeeksBefore ?? DEFAULT_REMINDER_WEEKS_BEFORE
  const reminderWeeksValue = reminderWeeksDraft ?? String(savedReminderWeeks)
  const parsedReminderWeeks = parseReminderWeeks(reminderWeeksValue)
  const reminderWeeksChanged =
    reminderWeeksDraft !== null && parsedReminderWeeks !== savedReminderWeeks

  const hasUnsavedChanges = notificationsDiff.length > 0 || reminderWeeksChanged

  useNavigateConfirm(
    hasUnsavedChanges,
    translations.prompt.unsavedChangesNavigate
  )

  const [patchNotifications, { loading: patchLoading }] = useMutation<
    BatchPatchNotificationSettingsMutation,
    BatchPatchNotificationSettingsMutationVariables
  >(BATCH_PATCH_NOTIFICATION_SETTINGS_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: ['CurrentCompanyNotificationSettings'],
    onCompleted() {
      addToast('success', translations.success)
      setNotificationsDiff([])
      setReminderWeeksDraft(null)
    },
    onError: onErrorAuto(translations.error),
  })

  function handleSave() {
    if (!queryData) return

    if (reminderWeeksChanged && parsedReminderWeeks === null) {
      addToast('error', translations.invalidReminderWeeks)
      return
    }

    const grouped = groupBy(notificationsDiff, 'row')
    const input: BatchPatchNotificationSettingInput[] = []

    for (const [key, values] of Object.entries(grouped)) {
      const value_object: { [x: string]: boolean | null } = {}
      for (const value of values) {
        value_object[value.column] = value.checked
      }
      input.push({
        id: key,
        ...value_object,
      } as BatchPatchNotificationSettingInput)
    }

    if (reminderWeeksChanged && driverCardSetting && parsedReminderWeeks) {
      const existing = input.find(entry => entry.id === driverCardSetting.id)
      if (existing) existing.reminderWeeksBefore = parsedReminderWeeks
      else
        input.push({
          id: driverCardSetting.id,
          reminderWeeksBefore: parsedReminderWeeks,
        })
    }
    void patchNotifications({
      variables: {
        input: input,
      },
    })
  }

  const loading = queryLoading || patchLoading

  return {
    data: queryData,
    loading,
    translations: exportedTranslations,
    showUnsavedChangesPopup: hasUnsavedChanges,
    handleSave,
    onChangeCallback: setNotificationsDiff,
    driverCardReminder: driverCardSetting && {
      value: reminderWeeksValue,
      invalid: parsedReminderWeeks === null,
      onChange: setReminderWeeksDraft,
    },
  }
}
